import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const OVERSIZE_ROW_WARNING = 15000;

/**
 * Parse a CSV or XLSX file into headers + row arrays.
 * @param {File} file
 * @returns {Promise<{ headers: string[], rows: string[][], rowCount: number, oversizeWarning: boolean }>}
 */
export async function parseImportFile(file) {
  if (!file) {
    throw new Error('No file selected');
  }

  const name = (file.name || '').toLowerCase();
  const isCsv = name.endsWith('.csv') || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';
  const isXlsx =
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    file.type === 'application/vnd.ms-excel';

  if (!isCsv && !isXlsx) {
    throw new Error('Please upload a CSV or Excel (.xlsx) file.');
  }

  if (isXlsx && !name.endsWith('.csv')) {
    return parseXlsxFile(file);
  }

  return parseCsvFile(file);
}

async function parseCsvFile(file) {
  const text = await file.text();
  return new Promise((resolve, reject) => {
    Papa.parse(text, {
      header: false,
      skipEmptyLines: 'greedy',
      complete: (results) => {
        try {
          const matrix = (results.data || []).filter(
            (row) => Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== '')
          );
          if (matrix.length === 0) {
            reject(new Error('File is empty'));
            return;
          }
          const headers = matrix[0].map((h, i) => {
            const label = String(h ?? '').trim();
            return label || `Column ${i + 1}`;
          });
          const rows = matrix.slice(1).map((row) =>
            headers.map((_, i) => String(row[i] ?? '').trim())
          );
          resolve({
            headers,
            rows,
            rowCount: rows.length,
            oversizeWarning: rows.length >= OVERSIZE_ROW_WARNING,
          });
        } catch (err) {
          reject(err);
        }
      },
      error: (err) => reject(err || new Error('Failed to parse CSV')),
    });
  });
}

async function parseXlsxFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('Excel file has no sheets');
  }
  const sheet = workbook.Sheets[sheetName];
  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false,
    blankrows: false,
  });

  const nonEmpty = (matrix || []).filter(
    (row) => Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== '')
  );
  if (nonEmpty.length === 0) {
    throw new Error('Spreadsheet is empty');
  }

  const headers = nonEmpty[0].map((h, i) => {
    const label = String(h ?? '').trim();
    return label || `Column ${i + 1}`;
  });
  const rows = nonEmpty.slice(1).map((row) =>
    headers.map((_, i) => String(row[i] ?? '').trim())
  );

  return {
    headers,
    rows,
    rowCount: rows.length,
    oversizeWarning: rows.length >= OVERSIZE_ROW_WARNING,
  };
}

const COLUMN_SUMMARY_MAX_DISTINCT = 40;
const COLUMN_SUMMARY_MAX_VALUE_LENGTH = 60;

/**
 * Summarize low-cardinality columns (stage/type/status/tag-style enums) as a
 * list of distinct values with counts. Used so column inference sees every
 * stage value in the file, not just the ones that happen to be in the first
 * few sample rows.
 *
 * @param {string[]} headers
 * @param {string[][]} rows
 * @param {{ maxDistinct?: number }} [options]
 * @returns {Record<string, Array<{ value: string, count: number }>>}
 */
export function summarizeColumnValues(headers, rows, options = {}) {
  const maxDistinct = options.maxDistinct ?? COLUMN_SUMMARY_MAX_DISTINCT;
  const summary = {};

  headers.forEach((header, colIndex) => {
    const counts = new Map();
    let overflow = false;

    for (const row of rows) {
      const raw = String(row?.[colIndex] ?? '').trim();
      if (!raw) continue;
      const value = raw.slice(0, COLUMN_SUMMARY_MAX_VALUE_LENGTH);
      if (!counts.has(value) && counts.size >= maxDistinct) {
        overflow = true;
        break;
      }
      counts.set(value, (counts.get(value) || 0) + 1);
    }

    if (overflow || counts.size === 0) return;

    summary[header] = Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
  });

  return summary;
}

export { OVERSIZE_ROW_WARNING };
